import { Injectable, signal } from '@angular/core';
import { Subject } from 'rxjs';

/**
 * DialogService — the app's own pop-ups (2026-09-29), replacing the browser's alert() / confirm() / prompt().
 *
 * Drawn by <app-dialog-host> (mounted once in app.component.html) to the canvas "Admin confirmations" board:
 * dimmed backdrop, white 20px-radius card, tinted icon tile, bold title, body text, buttons on the right
 * (a bottom sheet with 48px buttons on phones).
 *
 *   await this.dialog.alert('Bed released.');                                   // OK
 *   if (!(await this.dialog.confirm('Deactivate "Dengue NS1"?', { tone: 'danger', okText: 'Deactivate' }))) return;
 *   const reason = await this.dialog.prompt('Why is this test being cancelled?', { required: true });
 *   if (reason === null) return;                                                 // Cancel → null
 *
 * Unlike the browser versions these don't freeze the page, so code after an un-awaited alert() keeps running —
 * await it when the next line reloads, logs out or leaves the app. One dialog shows at a time; others queue.
 */
export type DialogTone = 'info' | 'success' | 'warning' | 'danger';

export interface DialogOptions {
  /** Bold heading. Default: the first line of a multi-line message, a short message itself, or a tone-based title. */
  title?: string;
  /** Icon tint + main button colour. Default: from a leading ✅/❌/⚠️ in the message, else info (teal). */
  tone?: DialogTone;
  /** Main button label. Default "OK" (alert) / "Confirm" (confirm) / "Submit" (prompt). */
  okText?: string;
  /** Secondary button label (confirm / prompt). Default "Cancel". */
  cancelText?: string;
}

export interface PromptOptions extends DialogOptions {
  defaultValue?: string;
  placeholder?: string;
  /** Label above the field (the message is shown as the body above it). */
  label?: string;
  /** Multi-line text box (reasons, notes). Enter then adds a new line instead of submitting. */
  multiline?: boolean;
  /** OK stays disabled until something other than spaces is typed. */
  required?: boolean;
  maxLength?: number;
  inputType?: 'text' | 'number' | 'email' | 'tel';
}

export interface DialogRequest {
  id: number;
  kind: 'alert' | 'confirm' | 'prompt';
  title: string;
  message: string;
  tone: DialogTone;
  okText: string;
  cancelText: string;
  prompt?: PromptOptions;
  resolve: (value: unknown) => void;
}

const TONE_TITLES: Record<DialogTone, string> = {
  info: 'Notice',
  success: 'Done',
  warning: 'Please check',
  danger: "Couldn't complete that",
};

@Injectable({ providedIn: 'root' })
export class DialogService {
  /** The dialog on screen now (null = none). Read by <app-dialog-host>. */
  readonly current = signal<DialogRequest | null>(null);
  /** Every answered dialog — its kind, tone and how it was answered. Never the title, message or typed text: titles
   *  often name the patient ("Cancel Asha's CBC?") and the user-journey log that listens is read by the platform owner. */
  readonly answered = new Subject<{ kind: DialogRequest['kind']; tone: DialogTone; answer: string }>();
  private queue: DialogRequest[] = [];
  private nextId = 1;

  alert(message: string, opts: DialogOptions = {}): Promise<void> {
    return this.open<void>('alert', message, opts);
  }

  confirm(message: string, opts: DialogOptions = {}): Promise<boolean> {
    return this.open<boolean>('confirm', message, opts);
  }

  /** Resolves with the typed text (possibly ''), or null when cancelled — same as the browser's prompt(). */
  prompt(message: string, opts: PromptOptions = {}): Promise<string | null> {
    return this.open<string | null>('prompt', message, opts, opts);
  }

  /** Called by the host when a button, Escape or the backdrop closes the current dialog. */
  close(result: unknown): void {
    const cur = this.current();
    if (!cur) return;
    cur.resolve(result);
    this.current.set(this.queue.shift() ?? null);
    const answer = cur.kind === 'alert' ? 'OK'
      : cur.kind === 'confirm' ? (result ? `Confirmed ("${cur.okText}")` : 'Cancelled')
      : (result === null ? 'Cancelled' : 'Submitted');
    this.answered.next({ kind: cur.kind, tone: cur.tone, answer });
  }

  /** Sign-out only. Drops the open and queued pop-ups WITHOUT answering them, so a question raised for one person
   *  (often naming a patient) is never shown to, or answered by, the next person, and no caller code after an awaited
   *  alert()/confirm() runs after sign-out. The callers' screens are being torn down. */
  discardAll(): void {
    this.queue = [];
    this.current.set(null);
  }

  /** TC-4533 (2026-11-03): the pop-up outlived the page. Back, then Confirm on the next screen, still sent the payment from the page just left.
   *  Back now answers every open question "Cancel". */
  cancelAll(): void {
    const answer = (d: DialogRequest) => d.resolve(d.kind === 'confirm' ? false : d.kind === 'prompt' ? null : undefined);
    const cur = this.current();
    const waiting = this.queue;
    this.queue = [];
    this.current.set(null);
    if (cur) answer(cur);
    waiting.forEach(answer);
  }

  private open<T>(kind: DialogRequest['kind'], message: string, opts: DialogOptions, prompt?: PromptOptions): Promise<T> {
    return new Promise<T>(resolve => {
      const { title, body, tone } = DialogService.describe(kind, String(message ?? ''), opts);
      // Double-click guard: the browser's confirm() froze the page, so a second click on "Confirm Payment" never
      // asked twice. Ours doesn't freeze, so an IDENTICAL dialog already showing or waiting means this call is a
      // repeat click — answer it "Cancel" at once (never a second "yes" that could send a payment twice).
      const same = (d: DialogRequest) => d.kind === kind && d.title === title && d.message === body;
      const cur = this.current();
      if ((cur && same(cur)) || this.queue.some(same)) {
        resolve((kind === 'confirm' ? false : kind === 'prompt' ? null : undefined) as T);
        return;
      }
      const req: DialogRequest = {
        id: this.nextId++,
        kind,
        title,
        message: body,
        tone,
        okText: opts.okText ?? (kind === 'alert' ? 'OK' : kind === 'confirm' ? 'Confirm' : 'Submit'),
        cancelText: opts.cancelText ?? 'Cancel',
        prompt,
        resolve: v => resolve(v as T),
      };
      if (this.current()) this.queue.push(req);
      else this.current.set(req);
    });
  }

  /** Title / body / tone for a message written for the browser's alert(): leading ✅ ❌ ⚠️ set the tone
   *  (and are dropped — the icon tile shows it), a multi-line message's first line becomes the title. */
  static describe(kind: DialogRequest['kind'], raw: string, opts: DialogOptions): { title: string; body: string; tone: DialogTone } {
    let text = raw.replace(/\r\n/g, '\n').trim();
    let tone: DialogTone | undefined = opts.tone;
    const lead = text.match(/^(✅|✔️|✔|❌|⛔|🚫|⚠️|⚠|ℹ️|ℹ|🔒|💡|📢)\s*/u);
    if (lead) {
      if (!tone) {
        if (/✅|✔/u.test(lead[1])) tone = 'success';
        else if (/❌|⛔|🚫/u.test(lead[1])) tone = 'danger';
        else if (/⚠/u.test(lead[1])) tone = 'warning';
      }
      text = text.slice(lead[0].length);
    }
    if (!tone) tone = kind === 'confirm' ? DialogService.confirmTone(text) : 'info';

    if (opts.title) return { title: opts.title, body: text, tone };
    const nl = text.indexOf('\n');
    if (nl > 0 && nl <= 120) {
      return { title: text.slice(0, nl).trim().replace(/[:：]$/, ''), body: text.slice(nl + 1).replace(/^\n+/, '').trim(), tone };
    }
    if (text.length <= 90) return { title: text, body: '', tone };
    return { title: kind === 'confirm' ? 'Please confirm' : kind === 'prompt' ? 'More details needed' : TONE_TITLES[tone], body: text, tone };
  }

  /** Red for actions that remove or undo things, amber for ones with money / clinical side effects. */
  private static confirmTone(text: string): DialogTone {
    if (/\b(delete|remove|deactivate|revoke|void|erase|discard|reject|suspend|disable|terminate|permanently)\b/i.test(text)) return 'danger';
    if (/\b(cancel|reverse|refund|write[- ]?off|override|force|discharge|lama|close|reset|retire|no[- ]show)\b/i.test(text)) return 'warning';
    return 'info';
  }
}
